import { ffsAssetModelSnapshotScopedProvider } from 'features/libra/stepComponents/ffs/FfsAssetModelSnapshotContext'
import { useWorkflow } from 'features/libra/WorkflowContext'
import { ffsWorkspaceStateScopedProvider } from 'features/libra/workspace/ffs/FfsWorkspaceStateContext'
import { FC, ReactNode } from 'react'

import type { WorkflowScopedProviderDescriptor } from './workflowScopedProviderTypes'

/**
 * Registry of workflow-specific context providers. This file is the
 * DELIBERATE feature-import boundary: it may import feature modules (FFS,
 * future NII specifics, ...) so that the generic shell (`LibraView`,
 * `WorkspaceLayoutRenderer`) never has to. Same role `workspacePanels/
 * index.ts` plays for panel components.
 *
 * Each descriptor decides for itself (via `appliesTo`) whether it mounts
 * for a given workflow -- the shell reads the workflow and injects only
 * what matches. To add a workflow-specific provider: export a descriptor
 * from your feature module and list it here.
 */
const WORKFLOW_SCOPED_PROVIDERS: ReadonlyArray<WorkflowScopedProviderDescriptor> =
  [ffsAssetModelSnapshotScopedProvider, ffsWorkspaceStateScopedProvider]

/**
 * Composes every registered provider whose `placement` matches and whose
 * `appliesTo(workflow)` returns true, in registry order (first listed =
 * outermost). Renders children directly when nothing matches.
 *
 * Context available to injected providers:
 *  - 'ticket' placement: `WorkflowProvider`, `RolodexEntriesWithSynced-
 *    RefreshesProvider`, `TicketWriterProvider`, `TicketSignoffsProvider`,
 *    `TicketLifecycleProvider`, and `TicketAccessProvider` are all mounted
 *    above the slot.
 *  - 'workspace' placement: additionally below `TicketStepContextProvider`
 *    and the `ActiveComponentProvider`/tab remount boundary. NOTE:
 *    `ComponentScopeContext` is present only on tabbed steps -- on untabbed
 *    steps `useComponentScope()` returns null.
 */
export const WorkflowScopedProviders: FC<{
  placement: WorkflowScopedProviderDescriptor['placement']
  children: ReactNode
}> = ({ placement, children }) => {
  const workflow = useWorkflow()
  const composed = WORKFLOW_SCOPED_PROVIDERS.filter(
    (descriptor) =>
      descriptor.placement === placement && descriptor.appliesTo(workflow)
  ).reduceRight<ReactNode>(
    (wrapped, { key, Provider }) => <Provider key={key}>{wrapped}</Provider>,
    children
  )
  return <>{composed}</>
}
